import { useTranslation } from "react-i18next";
import type { PackagingOption } from "~/domains/product/types/PackagingOption";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import { CollapsibleSection, CollapsedTitle } from "../shared/CollapsibleSection";
import { StockTable } from "../shared/StockTable";
import { ReplenishmentDate } from "../shared/ReplenishmentDate";
import { PerPackStack, SectionTitle } from "../shared/styled";
import { useAvailabilityEntries } from "../shared/useAvailabilityEntries";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const RESULT_NF = NumberFormatter.getFormatter();

interface InProductionSectionProps {
  pack: PackagingOption;
  expanded?: boolean;
  onToggle: () => void;
  showToggle?: boolean;
}

export const InProductionSection = ({ pack, expanded, onToggle, showToggle = true }: InProductionSectionProps) => {
  const { t } = useTranslation();
  const { transform } = useAvailabilityEntries();

  const inProductionEntries = transform(pack.stockLevels.inProduction, pack.stockLevels.inProductionWeekly);

  const clarificationEntries = pack.stockLevels.inClarification
    .filter((v) => v > 0)
    .map((v) => ({
      quantity: v,
      date: t("product.availability.stock.table.inClarification"),
    }));

  const allEntries = [...clarificationEntries, ...inProductionEntries];

  const firstUpcomingStock = inProductionEntries[0];
  const stockValue = firstUpcomingStock ? Number(firstUpcomingStock.quantity) : 0;

  const totalInProduction = allEntries.reduce((acc, entry) => acc + entry.quantity, 0);
  const canExpand = totalInProduction > 0;

  return (
    <CollapsibleSection
      expanded={expanded}
      onToggle={onToggle}
      showToggle={showToggle && canExpand}
      canExpand={canExpand}
      title={
        <>
          <PerPackStack direction={"row"} gap={1}>
            <CalendarTodayIcon  />
            {totalInProduction > 0 && (
              <SectionTitle>
                {t("productDetailsPage.availabilityTable.toCome", {
                  quantity: RESULT_NF.format(totalInProduction),
                })}
              </SectionTitle>
            )}
            {totalInProduction === 0 && <ReplenishmentDate date={pack.stockLevels.replenishmentLeadTimeDate} />}
          </PerPackStack>
          {!expanded && firstUpcomingStock && (
            <CollapsedTitle>
              {t("productDetailsPage.availabilityTable.upcoming", {
                quantity: isNaN(stockValue) ? firstUpcomingStock.quantity : RESULT_NF.format(stockValue),
                date: firstUpcomingStock.date,
              })}
            </CollapsedTitle>
          )}
        </>
      }
    >
      <StockTable warehouseName={pack.stockLevels.warehouseName} stock={allEntries} />
      <ReplenishmentDate date={pack.stockLevels.replenishmentLeadTimeDate} sx={{ mt: 2 }} />
    </CollapsibleSection>
  );
};
